import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAction, useMutation, useQuery } from "convex/react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { api } from "@/lib/convex-api";
import { formatUserFacingError } from "@/lib/errorMessages";
import { InviteSplitButton } from "@/components/invite-split-button";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import type { Id } from "../../../../backend/convex/_generated/dataModel";

interface TeamManagementProps {
  projectId: Id<"projects">;
}

const projectInviteSchema = z.object({
  email: z.string().trim().email("Enter a valid email address."),
  role: z.union([z.literal("owner"), z.literal("editor"), z.literal("viewer")]),
});

type ProjectInviteFormValues = z.infer<typeof projectInviteSchema>;

export const TeamManagement = ({ projectId }: TeamManagementProps) => {
  const currentUser = useQuery(api.users.me);
  const members = useQuery(api.projects.listMembers, { projectId }) ?? [];
  const role = useQuery(
    api.projects.getMemberRole,
    currentUser
      ? {
          projectId,
          userId: currentUser.id as Id<"users">,
        }
      : "skip",
  );
  const pendingInvites =
    useQuery(
      api.projects.listPendingInvites,
      role === "owner" ? { projectId } : "skip",
    ) ?? [];

  const createInviteLink = useMutation(api.projects.createInviteLink);
  const createInviteLinkAndSendEmail = useAction(
    api.projects.createInviteLinkAndSendEmail,
  );
  const updateMemberRole = useMutation(api.projects.updateMemberRole);
  const removeMember = useMutation(api.projects.removeMember);
  const revokeInvite = useMutation(api.projects.revokeInvite);
  const refreshInviteLink = useMutation(api.projects.refreshInviteLink);

  const [isInviting, setIsInviting] = useState(false);
  const inviteForm = useForm<ProjectInviteFormValues>({
    resolver: zodResolver(projectInviteSchema),
    defaultValues: {
      email: "",
      role: "editor",
    },
  });

  const isOwner = role === "owner";
  const ownerCount = members.filter((member) => member.role === "owner").length;

  const copyInviteLink = async (inviteUrl: string) => {
    const absoluteUrl = new URL(
      inviteUrl,
      globalThis.location.origin,
    ).toString();
    try {
      await globalThis.navigator.clipboard.writeText(absoluteUrl);
      toast.success("Invite link copied.");
    } catch {
      toast.error("Failed to copy invite link.");
    }
  };

  const onCreateInvite = async (
    values: ProjectInviteFormValues,
    sendEmail: boolean,
  ) => {
    setIsInviting(true);
    try {
      const email = values.email.trim();
      const result = sendEmail
        ? await createInviteLinkAndSendEmail({
            projectId,
            email,
            role: values.role,
          })
        : await createInviteLink({
            projectId,
            email,
            role: values.role,
          });
      await copyInviteLink(result.inviteUrl);
      inviteForm.reset({
        email: "",
        role: values.role,
      });
      if (sendEmail && "emailSent" in result && !result.emailSent) {
        toast.info(
          "Invite link created, but no email was sent. Link copied instead.",
        );
      }
    } catch (error) {
      toast.error(formatUserFacingError(error, "Failed to create invite."));
    } finally {
      setIsInviting(false);
    }
  };

  return (
    <div className="space-y-3 rounded-md border bg-muted/20 p-3 text-xs">
      <div className="space-y-2">
        <p className="font-medium text-foreground">Members</p>
        <div className="space-y-1">
          {members.map((member) => {
            const isLastOwner = member.role === "owner" && ownerCount <= 1;
            return (
              <div
                key={member.id}
                className="flex items-center justify-between rounded border bg-background px-2 py-1.5"
              >
                <div className="min-w-0">
                  <p className="truncate text-xs font-medium">{member.name}</p>
                  <p className="truncate text-[11px] text-muted-foreground">
                    {member.email}
                  </p>
                </div>
                <div className="ml-2 flex items-center gap-2">
                  {isOwner ? (
                    <Select
                      value={member.role}
                      onValueChange={(value: "owner" | "editor" | "viewer") => {
                        if (isLastOwner && value !== "owner") return;
                        void updateMemberRole({
                          projectId,
                          userId: member.userId,
                          role: value,
                        }).catch((error) => {
                          toast.error(
                            formatUserFacingError(
                              error,
                              "Failed to update role.",
                            ),
                          );
                        });
                      }}
                    >
                      <SelectTrigger className="h-7 w-24 text-[11px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="owner">Owner</SelectItem>
                        {!isLastOwner && (
                          <SelectItem value="editor">Editor</SelectItem>
                        )}
                        {!isLastOwner && (
                          <SelectItem value="viewer">Viewer</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="rounded border px-1.5 py-0.5 text-[11px] capitalize">
                      {member.role}
                    </span>
                  )}
                  {isOwner && !isLastOwner && (
                    <Button
                      size="xs"
                      variant="ghost"
                      className="text-destructive"
                      onClick={() => {
                        void removeMember({
                          projectId,
                          userId: member.userId,
                        }).catch((error) => {
                          toast.error(
                            formatUserFacingError(
                              error,
                              "Failed to remove member.",
                            ),
                          );
                        });
                      }}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {isOwner && (
        <>
          <div className="h-px bg-border" />
          <div className="space-y-2">
            <p className="font-medium text-foreground">Invite people</p>
            <Form {...inviteForm}>
              <form
                onSubmit={(event) => event.preventDefault()}
                className="space-y-2"
              >
                <div className="flex items-start gap-2">
                  <FormField
                    control={inviteForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem className="flex-1 gap-1">
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="user@company.com"
                            className="h-8 text-xs"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage className="text-[11px]" />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={inviteForm.control}
                    name="role"
                    render={({ field }) => (
                      <FormItem className="w-24 gap-1">
                        <Select
                          value={field.value}
                          onValueChange={(value) =>
                            field.onChange(
                              value as ProjectInviteFormValues["role"],
                            )
                          }
                        >
                          <FormControl>
                            <SelectTrigger size="sm" className="w-24 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="owner">Owner</SelectItem>
                            <SelectItem value="editor">Editor</SelectItem>
                            <SelectItem value="viewer">Viewer</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                  <InviteSplitButton
                    primaryLabel={
                      isInviting ? "Working..." : "Send invite email"
                    }
                    secondaryLabel="Create invite link"
                    size="sm"
                    className="self-start"
                    disabled={isInviting}
                    onPrimaryAction={() => {
                      void inviteForm.handleSubmit((values) =>
                        onCreateInvite(values, true),
                      )();
                    }}
                    onSecondaryAction={() => {
                      void inviteForm.handleSubmit((values) =>
                        onCreateInvite(values, false),
                      )();
                    }}
                  />
                </div>
              </form>
            </Form>
          </div>
          <div className="space-y-1">
            <p className="font-medium text-foreground">Pending invites</p>
            {pendingInvites.length === 0 ? (
              <p className="text-[11px] text-muted-foreground">
                No pending invites.
              </p>
            ) : (
              pendingInvites.map((invite) => (
                <div
                  key={invite.id}
                  className="flex items-center justify-between rounded border bg-background px-2 py-1.5"
                >
                  <div className="min-w-0">
                    <p className="truncate">{invite.email}</p>
                    <p className="text-[11px] text-muted-foreground capitalize">
                      {invite.role} • {invite.isExpired ? "Expired" : "Pending"}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => {
                        void copyInviteLink(invite.inviteUrl);
                      }}
                    >
                      Copy link
                    </Button>
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => {
                        void refreshInviteLink({ inviteId: invite.id })
                          .then((result) => {
                            void copyInviteLink(result.inviteUrl);
                            toast.success("Invite link refreshed.");
                          })
                          .catch((error) => {
                            toast.error(
                              formatUserFacingError(
                                error,
                                "Failed to refresh invite link.",
                              ),
                            );
                          });
                      }}
                    >
                      Refresh link
                    </Button>
                    <Button
                      size="xs"
                      variant="ghost"
                      className="text-destructive"
                      onClick={() => {
                        void revokeInvite({ inviteId: invite.id }).catch(
                          (error) => {
                            toast.error(
                              formatUserFacingError(
                                error,
                                "Failed to revoke invite.",
                              ),
                            );
                          },
                        );
                      }}
                    >
                      Revoke
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};
